import { useMemo } from "react";

import { CCPOfferingContent } from "src/contentful/product-catalog";

import {
    CloudOfferingKey,
    CloudProductKey,
    CurrencyISOCode,
    DefaultPlanLimits,
    FormattedPriceCalculationResponse,
    OfferingHamsMap,
    OfferingSKUMap,
    PriceCalculationRequest,
    PricingPeriod,
    PricingPlans,
    ProductPlan,
    SpecialPlanNames
} from "../constants";

import { getFormattedPrice } from "./amounts-format";
import { extractPricingTiersFromEntry } from "./extract-pricing-tiers-from-entry";
import { calculatePrice } from "./price-calculation";

export const getFreePriceCalc = (
    currencyISOCode: CurrencyISOCode
): FormattedPriceCalculationResponse => {
    const zeroPrice = getFormattedPrice(currencyISOCode, 0);

    return {
        totalPrice: 0,
        avgPerUnitPrice: 0,
        avgPerUnitPriceWithDiscount: 0,
        formattedTotalPrice: zeroPrice,
        formattedAvgPerUnitPrice: zeroPrice,
        formattedAvgPerUnitPriceWithDiscount: zeroPrice,
        formattedTotalPriceWithDiscount: zeroPrice,
        hasFlatRateFirstTier: true,

        breakdown: [
            {
                unitsInTier: 0,
                unitsAvailableInTier: 0,

                unitLimitLower: 0,
                unitLimitUpper: 0,

                totalPriceForTier: 0,
                avgPerUnitPriceForTier: 0
            }
        ],
        formattedBreakdown: [
            {
                formattedUnitsInTier: "0",
                formattedUnitsAvailableInTier: "0",

                formattedUnitLimitLower: "0",
                formattedUnitLimitUpper: "0",

                formattedTotalPriceForTier: zeroPrice,
                formattedAvgPerUnitPriceForTier: zeroPrice
            }
        ]
    };
};

export const usePriceFinderRequests = (
    productKey: string,
    productPlans: ProductPlan[],
    unitMinEnterprise: number,
    unitCount: number,
    billingCycle: PricingPeriod
): PriceCalculationRequest[] => {
    return useMemo(() => {
        const getNumUnits = (planName: string, signupUnitLimit: number) => {
            let adjNumUnits = 0;

            // following scenarios do not have pricing and should send a zero unit count,
            // the zero pricing will not be displayed and is needed only to retain the
            // correct array order of the pricing requests/responses

            // Free plan has no pricing
            const isFree = planName === SpecialPlanNames.FREE;

            // Enterprise plans do not have monthly pricing
            const isMonthlyEnterprise =
                planName === SpecialPlanNames.ENTERPRISE &&
                billingCycle === "MONTHLY";

            // Enterprise plans have a minimum unit count
            const isUnmetUnitCountEnterprise =
                planName === SpecialPlanNames.ENTERPRISE &&
                unitCount < unitMinEnterprise;
            const isOverSignupUnitLimit = unitCount > signupUnitLimit;

            if (
                ![
                    isFree,
                    isMonthlyEnterprise,
                    isUnmetUnitCountEnterprise,
                    isOverSignupUnitLimit
                ].includes(true)
            ) {
                adjNumUnits = unitCount;
            }

            // keep unit count to general maximum limit
            return Math.min(adjNumUnits, signupUnitLimit);
        };

        const planPriceReqs: PriceCalculationRequest[] = productPlans.map(
            ({ offeringSku, planName, signupLimit }) => ({
                ccpOfferingSKU: offeringSku,
                hamsProductKey: (productKey +
                    (planName === SpecialPlanNames.STANDARD
                        ? ""
                        : `.${planName.toLowerCase()}`)) as CloudProductKey,
                numUnits: getNumUnits(
                    planName,
                    signupLimit || DefaultPlanLimits.UNIT_LIMIT
                ),
                period: billingCycle
            })
        );

        return planPriceReqs;
    }, [productPlans, productKey, billingCycle, unitCount, unitMinEnterprise]);
};

export const usePricing = (
    ccpPricingData: OfferingSKUMap<CloudOfferingKey, CCPOfferingContent>,
    hamsPricingData: OfferingHamsMap<CloudProductKey, PricingPlans>,
    getPriceRequests: readonly PriceCalculationRequest[],
    currencyISOCode: CurrencyISOCode,
    localeISOCode: string
): FormattedPriceCalculationResponse[] => {
    const priceCalculations = [];

    for (const {
        ccpOfferingSKU,
        hamsProductKey,
        numUnits,
        period
    } of getPriceRequests) {
        if (numUnits === 0) {
            priceCalculations.push(getFreePriceCalc(currencyISOCode));
        } else {
            const pricingTiers = extractPricingTiersFromEntry(
                ccpPricingData,
                hamsPricingData,
                period,
                currencyISOCode,
                ccpOfferingSKU,
                hamsProductKey
            );

            if (!pricingTiers) {
                console.log(`Pricing tiers not found for ${period}.`);
                priceCalculations.push(getFreePriceCalc(currencyISOCode));
            } else {
                priceCalculations.push(
                    calculatePrice(
                        numUnits,
                        pricingTiers,
                        currencyISOCode,
                        period,
                        localeISOCode
                    )
                );
            }
        }
    }

    return priceCalculations;
};
